// Synthesized admin alert sounds using the Web Audio API — no external assets,
// zero network cost, distinct timbres so the owner can tell events apart by ear.
//
// Browsers block audio until the user interacts with the page, so `unlock()` must
// be called from a user gesture (e.g. clicking the "Enable alerts" toggle).

let ctx = null;
let unlocked = false;

export function unlock() {
  if (unlocked && ctx?.state === "running") return true;
  try {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    unlocked = true;
    return true;
  } catch (_e) {
    return false;
  }
}

export function isUnlocked() {
  return unlocked && ctx?.state === "running";
}

function tone({ freq, duration = 0.15, type = "sine", gain = 0.18, startAt = 0 }) {
  if (!ctx) return;
  const t0 = ctx.currentTime + startAt;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

// Bright two-tone ding — E5 then B5. Celebratory, quick.
export function playNewOrder() {
  if (!isUnlocked()) return;
  tone({ freq: 659.25, duration: 0.20, type: "sine", gain: 0.22, startAt: 0.00 });
  tone({ freq: 987.77, duration: 0.32, type: "sine", gain: 0.22, startAt: 0.15 });
}

// Attention-grabbing 3-note ascending arpeggio (C5-E5-G5) with a longer sustain.
export function playBulkOrder() {
  if (!isUnlocked()) return;
  tone({ freq: 523.25, duration: 0.22, type: "triangle", gain: 0.22, startAt: 0.00 });
  tone({ freq: 659.25, duration: 0.22, type: "triangle", gain: 0.22, startAt: 0.14 });
  tone({ freq: 783.99, duration: 0.46, type: "triangle", gain: 0.24, startAt: 0.28 });
}

// Gentle single bell — A5 fundamental + faint high harmonic.
export function playNewReview() {
  if (!isUnlocked()) return;
  tone({ freq: 880.00, duration: 0.60, type: "sine", gain: 0.17, startAt: 0.00 });
  tone({ freq: 1318.51, duration: 0.36, type: "sine", gain: 0.09, startAt: 0.03 });
}

// Subtle short blip — for status changes and everything else.
export function playUpdate() {
  if (!isUnlocked()) return;
  tone({ freq: 523.25, duration: 0.11, type: "sine", gain: 0.15, startAt: 0.00 });
}
